import React, { useState, useEffect } from 'react';
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { updateCustomTextEntity } from 'views/App/Portal/Policy/DraftManuals/_data_sources/customTextEntities/manageCustomTextEntities';
import errorHelper from 'helpers/errorHelper';
import type {
  CreateCustomTextEntityRequest,
  CustomTextEntity,
} from 'views/App/Portal/Policy/_types/customTextEntities';
import type { ServerErrorResponse } from 'data_sources/constants';

interface EditCustomTextEntityModalProps {
  open: boolean;
  onClose: () => void;
  manualId: string;
  textEntityId: string;
  currentEntity: CustomTextEntity;
  onSuccess: (updatedEntity: CustomTextEntity) => void;
}

export function EditCustomTextEntityModal({
  open,
  onClose,
  manualId,
  textEntityId,
  currentEntity,
  onSuccess,
}: EditCustomTextEntityModalProps): JSX.Element {
  const [isUpdatingCustomEntity, setIsUpdatingCustomEntity] = useState(false);
  const [customEntityForm, setCustomEntityForm] = useState<CreateCustomTextEntityRequest>({
    textEntity: '',
    substitutionValue: '',
    description: '',
  });
  const [customEntityErrors, setCustomEntityErrors] = useState<ServerErrorResponse | null>(null);

  // Initialize form with current entity data when modal opens
  useEffect(() => {
    if (open && currentEntity) {
      // Remove CUSTOM: prefix for display in the form
      const textEntityWithoutPrefix = currentEntity.textEntity.startsWith('CUSTOM:')
        ? currentEntity.textEntity.substring(7) // Remove 'CUSTOM:' (7 characters)
        : currentEntity.textEntity;

      setCustomEntityForm({
        textEntity: textEntityWithoutPrefix,
        substitutionValue: currentEntity.substitutionValue,
        description: currentEntity.description,
      });
      setCustomEntityErrors(null);
    }
  }, [open, currentEntity]);

  const handleCancel = () => {
    setCustomEntityErrors(null);
    onClose();
  };

  const handleSave = async () => {
    if (!manualId || !textEntityId) return;

    // Validate required fields
    const trimmedTextEntity = customEntityForm.textEntity.trim().replace(/\s+/g, '');
    const trimmedSubstitutionValue = customEntityForm.substitutionValue.trim();

    if (!trimmedTextEntity || !trimmedSubstitutionValue) {
      return;
    }

    setIsUpdatingCustomEntity(true);
    setCustomEntityErrors(null);

    try {
      // Add CUSTOM: prefix if not already present
      const textEntityWithPrefix = trimmedTextEntity.startsWith('CUSTOM:')
        ? trimmedTextEntity
        : `CUSTOM:${trimmedTextEntity}`;

      const formDataWithPrefix = {
        ...customEntityForm,
        textEntity: textEntityWithPrefix,
        substitutionValue: trimmedSubstitutionValue,
      };

      const updatedCustomEntity = await updateCustomTextEntity(manualId, textEntityId, formDataWithPrefix);

      // Call the success callback
      onSuccess(updatedCustomEntity);

      // Close modal
      handleCancel();
    } catch (error: unknown) {
      if (typeof error === 'object' && error !== null && 'errorDetails' in error) {
        setCustomEntityErrors(error as ServerErrorResponse);
      } else {
        setCustomEntityErrors({
          errorMessage: 'An unexpected error occurred',
          errorDetails: [],
        } as ServerErrorResponse);
      }
    } finally {
      setIsUpdatingCustomEntity(false);
    }
  };

  const handleFormChange = (field: keyof CreateCustomTextEntityRequest, value: string) => {
    setCustomEntityForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth='sm'
      fullWidth
    >
      <DialogTitle>Edit Custom Text Entity</DialogTitle>
      <DialogContent>
        {customEntityErrors && errorHelper.renderAllErrors(customEntityErrors)}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label='Text Entity Name'
            value={customEntityForm.textEntity}
            onChange={(e) => handleFormChange('textEntity', e.target.value)}
            fullWidth
            required
          />
          <TextField
            label='Text Entity Value'
            value={customEntityForm.substitutionValue}
            onChange={(e) => handleFormChange('substitutionValue', e.target.value)}
            fullWidth
            required
          />
          <TextField
            label='Description'
            value={customEntityForm.description}
            onChange={(e) => handleFormChange('description', e.target.value)}
            fullWidth
            multiline
            rows={3}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          variant='outlined'
          color='neutral'
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant='contained'
          disabled={isUpdatingCustomEntity || !customEntityForm.textEntity || !customEntityForm.substitutionValue}
        >
          {isUpdatingCustomEntity ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
